import React from "react";
import { Link } from "react-router-dom";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { PageHeader, KpiTile, SectionCard, StatePill } from "../../components/Ui";
import { AlertTriangle, FileText, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function PurchaserDashboard() {
  const { data, reload } = usePageBootstrap({ path: "/dashboard/bootstrap", wsTypes: ["purchase"] });
  useBootstrapWS(["purchase"], reload);

  const barg = data?.bargaining_queue || [];
  const recent = data?.recent_activity || [];

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="purchaser-dashboard">
      <PageHeader
        eyebrow="Procurement"
        title="Purchasing Desk"
        description="Contract coverage, bargaining decisions, and recent inward trucks."
        actions={
          <Button asChild className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-11">
            <Link to="/contracts"><FileText className="h-4 w-4 mr-2" />Contracts</Link>
          </Button>
        }
      />

      {barg.length > 0 && (
        <div className="border-l-4 border-bansal-gold bg-amber-50 rounded-r-xl px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <AlertTriangle className="h-5 w-5 text-bansal-goldDark mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-amber-900">
                {barg.length} purchase{barg.length > 1 ? "s" : ""} awaiting bargaining decision
              </div>
              <p className="text-sm text-amber-800/80 mt-1">Lab results exceeded contract thresholds.</p>
            </div>
          </div>
          <Link
            to="/purchases"
            className="shrink-0 text-center px-4 py-2.5 bg-bansal-sage text-white text-xs uppercase tracking-widest rounded-md hover:bg-bansal-sageDark min-h-[44px] inline-flex items-center justify-center"
          >
            Resolve
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <KpiTile label="Bargaining" value={barg.length} sub="Needs decision" accent testId="pur-kpi-bargain" />
        <KpiTile label="Recent inward" value={recent.length} sub="Latest purchases" testId="pur-kpi-recent" />
        <KpiTile label="In negotiation" value={barg.length} sub="Awaiting decision" testId="pur-kpi-neg" />
      </div>

      <SectionCard title="Recent purchases" testId="pur-recent">
        {recent.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No recent purchases.</p>
        ) : (
          <div className="space-y-2">
            {recent.slice(0, 8).map((p) => (
              <Link
                key={p.id}
                to={`/purchases/${p.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-3 hover:bg-accent/60"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{p.party_name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{p.code} · {p.variety}</div>
                </div>
                <StatePill state={p.state} />
              </Link>
            ))}
          </div>
        )}
        <Link to="/purchases" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
          View all purchases <ArrowRight className="h-3 w-3" />
        </Link>
      </SectionCard>
    </div>
  );
}
